<template>
  <div class="u-mb--m">
    <div class="c-hands-free-intro u-flex u-flex-align-center u-mb--s">
      <h6 class="u-mr--xs u-mb--0">
        {{ translations.intro }}
      </h6>
      <span
        ref="tooltip"
        class="c-hands-free-tooltip u-flex u-flex-align-baseline"
      >
        <Icon
          :icon="iconHelp"
          :size="16"
          @click="isTooltipVisible = !isTooltipVisible"
        />
        <transition name="fade">
          <span
            v-if="isTooltipVisible"
            class="tooltip-content"
          >
            <strong class="u-text-uppercase u-font-size-large u-font-bold u-flex u-mb--xs u-flex-justify-space-between">
              {{ translations.tooltip.title }}
              <span>
                <Icon
                  :icon="iconClose"
                  :size="24"
                  @click="isTooltipVisible = false"
                />
              </span>
            </strong>
            <span class="u-font-size-medium">{{ translations.tooltip.text }}</span>
          </span>
        </transition>
      </span>
    </div>
    <button
      v-if="currentStage !== 'listening' && !isListeningStage"
      class="c-button c-button--handsfree"
      @click="startHandsFreeFlow"
    >
      <Icon
        :icon="micIcon"
        :size="24"
      />
      {{ translations.letsCook }}
    </button>
    <button
      v-if="isListeningStage"
      class="c-button c-button--handsfree"
      @click="finishHandsFreeFlow"
    >
      <Icon
        :icon="micIcon"
        :size="24"
      />
      {{ translations.stopCooking }}
    </button>
  </div>
  <Teleport to="body">
    <div
      class="c-hands-free-popup"
      :class="{
        'opened': isPopupOpened,
        'not-visible': currentStage === null
      }"
    >
      <template v-if="currentStage && currentStage.includes('introduction')">
        <introduction
          :translations="translations.introduction"
          :is-loading="isLoading"
          :is-close-state="true"
          @toggle-popup="closeHandsFreeFlow"
          @begin-listening="beginListening"
        />
      </template>
      <template v-if="currentStage === 'not-allowed'">
        <popup
          :translations="translations.notAllowed"
          :is-loading="isLoading"
          :is-close-state="true"
          @toggle-popup="closeHandsFreeFlow"
          @button-action="requestMicrophoneAccess"
        />
      </template>
      <template v-if="currentStage === 'listening'">
        <popup
          :translations="translations.listening"
          :is-loading="isLoading"
          @toggle-popup="openHelp"
        />
      </template>
      <template v-if="isHelpStage">
        <popup
          :translations="translations.help"
          :is-loading="isLoading"
          @toggle-popup="continueListening"
          @button-action="finishHandsFreeFlow"
        />
      </template>
      <template v-if="isNotRecognizedStage">
        <popup
          additional-classname="not-recognized"
          :translations="translations.notRecognized"
          :is-loading="isLoading"
          @toggle-popup="openHelp"
        />
      </template>
      <template v-if="isNotRecognizedStageError">
        <popup
          additional-classname="not-recognized error-state"
          :translations="translations.notRecognizedError"
          :is-loading="isLoading"
          @toggle-popup="openHelp"
        />
      </template>
      <template v-if="currentStage === 'not-supported'">
        <popup
          additional-classname="hide-chevron"
          :translations="translations.notSupported"
          :is-loading="isLoading"
          @button-action="closeHandsFreeFlow"
        />
      </template>
      <template v-if="isAlmostDone">
        <popup
          :translations="translations.almostDone"
          :is-loading="isLoading"
          @toggle-popup="togglePopup"
        />
      </template>
      <template v-if="currentStage === 'finish'">
        <finish
          :translations="translations.finish"
          @close-hands-free-flow="closeHandsFreeFlow"
          @toggle-popup="togglePopup"
        />
      </template>
      <button
        id="fakeButton"
        style="position: absolute; left: -9999px; opacity: 0;"
        @click="runCommand"
      >
        Run Command
      </button>
    </div>
  </Teleport>
  <div
    v-if="getStatus"
    class="hands-free-logging"
  >
    <div>Recognition lang: <strong>{{ selectedLanguage }}</strong></div>
    <div>WebSpeechApi Status: <strong>{{ printWebSpeechStatus }}</strong></div>
    <div>WebSpeechApi Error: <strong>{{ printWebSpeechError }}</strong></div>
    <div>Speech: <strong>{{ printRecognizedSpeech }}</strong></div>
    <div>Current stage: <strong>{{ currentStage }}</strong></div>
  </div>
</template>

<script>
import {
  ref, onMounted, onUnmounted, computed
} from 'vue'
import {
  state, toggleHelpStage,
  toggleListening,
  togglePopupState,
  updateCurrentStage
} from '@/composition/voiceState'
import Icon from '@/components/Icon.vue'
import micIcon from '@/assets/img/icons/mic.svg'
import iconHelp from '@/assets/img/icons/help.svg'
import iconClose from '@/assets/img/icons/close.svg'

import Popup from '@/components/recipe/handFreeCookingStages/Popup.vue'
import Introduction from '@/components/recipe/handFreeCookingStages/Introduction.vue'
import Finish from '@/components/recipe/handFreeCookingStages/Finish.vue'
import tracking from '@/utils/gtm-util'
import 'regenerator-runtime'
import WebSpeechRecognizer from '@/services/web_speech_recognizer'

import useCommands from '@/composition/useCommands'

export default {
  components: {
    Finish,
    Popup,
    Introduction,
    Icon
  },
  props: {
    model: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const translationsFallback = {
      intro: 'Handsfree cooking',
      letsCook: 'Let\'s cook',
      stopCooking: 'Stop cooking',
      tooltip: {
        title: 'Handsfree cooking',
        text: 'Handsfree cooking is our voice activated assistant that helps you through steps of the recipe. Simply say e.g. “Go to next step” – this way you can focus on cooking, not scrolling.'
      },
      introduction: {
        title: 'Introduction',
        content: '<p>In order for this feature to work we need access to your microphone.</p><p>Read more about how your data is used in our <a href="#">Privacy policy</a>.</p>',
        buttonText: 'Allow microphone access',
        steps: [
          '<p>Commands<br>Navigate between steps using commands such as “Go to next step” or “Go to ingredients” to navigate back and forth.</p><p>When you\'re done with this introduction, you can open this menu to find a full list of commands.</p><p>We’d recommend reducing background noise such as music and machinery as much for the best possible experience. </p>',
          '<p>Preperation<br>We’d recommend preparing all your ingredients  beforehand.</p><p>Whenever you’re ready say “Let’s cook” or simply press the right arrow button below.</p>'
        ]
      },
      notAllowed: {
        title: 'Something went wrong',
        content: '<p>In order to use handsfree cooking, we need access to your microphone.</p>',
        buttonText: 'Click here to allow access'
      },
      listening: {
        title: 'Ready for instructions'
      },
      help: {
        title: 'List of commands',
        content: '<p>Are you stuck? One of these commands might help you move on.</p><span>Commands</span><ul><li>Next step</li><li>Previous step</li><li>Scroll up</li><li>Scroll down</li><li>Go to ingredients</li><li>Go to method</li><li>Help</li></ul>',
        buttonText: 'Stop cooking'
      },
      finish: {
        title: 'You’re done!',
        content: '<p>Enjoy your meal!</p><p>How was your experience using handsfree cooking?</p>',
        buttonText: 'Close handsfree cooking',
        voteUp: 'Very helpful',
        voteDown: 'Needs tweaking',
        successMessage: 'Your feedback is appreciated!'
      },
      notSupported: {
        title: 'Browser not supported',
        content: '<span>This feature is unfortunately not supported in Firefox. Use any of the following browsers to get started with handsfree cooking:</span><ul><li>Google Chrome</li><li>Safari</li><li>Microsoft Edge</li></ul>',
        buttonText: 'Close handsfree cooking'
      },
      notRecognized: {
        title: 'Command not recognized'
      },
      notRecognizedError: {
        title: 'Say "Help" to see commands'
      },
      almostDone: {
        title: 'Almost there',
        content: '<p>You\'ve made it to the last bit!</p><p>Continue by saying "I\'m done" so we can make sure to send you off right.</p>'
      }
    }

    const speechRecognizer = ref(null)
    const selectedLanguage = ref(document.documentElement.lang || 'en')
    const commands = ref(useCommands(props.model.commands))
    const isTooltipVisible = ref(false)
    const tooltip = ref(null)
    const lastCommand = ref(null)
    const counter = ref(0)
    const isLoading = ref(false)
    const isError = ref(false)
    const webSpeechFeatureStatus = ref(null)

    const isPopupOpened = computed(() => state.isPopupOpened)
    const isHelpStage = computed(() => state.isHelpStage)
    const currentStage = computed(() => state.currentStage)

    const isListeningStage = computed(() => currentStage.value && currentStage.value.includes('listening'))
    const isNotRecognizedStage = computed(() => currentStage.value && currentStage.value.includes('not-recognized'))
    const isNotRecognizedStageError = computed(() => currentStage.value && currentStage.value.includes('recognized-error'))
    const isAlmostDone = computed(() => currentStage.value && currentStage.value.includes('almost-done'))
    const translations = computed(() => (props.model ? props.model : translationsFallback))

    const printRecognizedSpeech = ref(null)
    const printWebSpeechStatus = ref(null)
    const printWebSpeechError = ref(null)
    const getStatus = ref(false)

    let hasTrackedInitialStart = false
    let flowFinished = false
    let restarted = false
    /*eslint-disable*/

    function restartRecognition() {
      if (!speechRecognizer.value || flowFinished) return

      if (isRecognizing.value) {
        speechRecognizer.value.stop()
      }

      setTimeout(() => {
        if (!isRecognizing.value) {
          try {
            speechRecognizer.value.start()
            console.log('[HandsFree] Restarted recognition after pause')
          } catch (e) {
            console.warn('[HandsFree] First restart failed, retrying...', e)
            setTimeout(() => {
              try {
                if (!isRecognizing.value) {
                  speechRecognizer.value.start()
                  console.log('[HandsFree] Restarted after second attempt')
                }
              } catch (e2) {
                console.error('[HandsFree] Second restart failed:', e2)
              }
            }, 1000)
          }
        }
      }, 500)
    }

    setInterval(() => {
      if (
        webSpeechFeatureStatus.value === 'stopped' &&
        currentStage.value === 'listening' &&
        !flowFinished &&
        !restarted &&
        !isRecognizing.value
      ) {
        restartRecognition()
        restarted = true
        console.log('Manually restarted recognition')
      }

      if (webSpeechFeatureStatus.value === 'listening' && restarted) {
        restarted = false
      }
    }, 1000)

    const handleClickOutside = (event) => {
      if (tooltip.value && !tooltip.value.contains(event.target)) {
        isTooltipVisible.value = false
      }
    }

    const handleCommand = (recognizedSpeech) => {
      let foundCommand = null
      const speech = recognizedSpeech

      printRecognizedSpeech.value = recognizedSpeech

      Object.keys(commands.value).forEach((alias) => {
        if (speech.includes(alias.toLowerCase())) {
          foundCommand = alias
        }
      })

      if (foundCommand) {
        if (!isAlmostDone.value) {
          toggleHelpStage(false)
          togglePopupState(false)
          updateCurrentStage('listening')
        }
        if (isAlmostDone.value && props.model.commands.imDone.includes(foundCommand)) {
          updateCurrentStage('finish')
          /* eslint-disable-next-line */
          finishListening()
          tracking.handsfreeEndFeature('completed recipe flow')
        }
        lastCommand.value = foundCommand
        counter.value = 0

        requestAnimationFrame(() => {
          document.getElementById('fakeButton')?.click()
        })
      } else {
        counter.value += 1
        if (!currentStage.value.includes('help')) {
          updateCurrentStage('listening not-recognized')
          toggleHelpStage(false)
          togglePopupState(false)
        }

        if (counter.value > 3) {
          updateCurrentStage('listening recognized-error')
        }
      }
    }

    const handleIntroduction = (command) => {
      if (props.model.commands.letsCook.includes(command) && commands.value[command]) {
        lastCommand.value = command
        setTimeout(() => {
          document.getElementById('fakeButton')?.click()
        }, 100)
      }
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

    const startHandsFreeFlow = () => {
      flowFinished = false
      updateCurrentStage('introduction')
      tracking.handsfreeActivateFeature()

      if (!SpeechRecognition) {
        tracking.handsfreeOnboardingErrorMessage('browser not supported')
        updateCurrentStage('not-supported')
      } else {
        let hasStarted = false

        speechRecognizer.value = WebSpeechRecognizer.init({
          lang: selectedLanguage.value,
          continuesRecognition: true,
          onUserSpeech: (rec) => {
            isRecognizing.value = ['starting', 'recording'].includes(rec.status)
            if (['stopped', 'aborted', 'error'].includes(rec.status)) {
              isRecognizing.value = false
            }
            webSpeechFeatureStatus.value = rec.status
            console.log(rec)
            printWebSpeechStatus.value = rec.status
            printWebSpeechError.value = rec.error

            if (!hasStarted && rec.status === 'starting') {
              hasStarted = true
              return
            }

            if (rec.status === 'error' && (rec.error === 'not-allowed' || rec.error === 'service-not-allowed')) {
              isError.value = true
              updateCurrentStage('not-allowed')
              toggleListening(false)
              speechRecognizer.value.stop()
              return
            }

            if (rec.status === 'recording') {
              toggleListening(true)
              if (!hasTrackedInitialStart) {
                tracking.handsfreeAllowMicrophoneAccess()
                hasTrackedInitialStart = true
              }

              if (rec.transcriptions.length && !rec.finalTranscriptions) {
                isLoading.value = true
              }

              if (rec.finalTranscriptions) {
                isLoading.value = false
                const speech = rec.transcriptions[0].text.toLowerCase().trim()

                if (currentStage.value.includes('introduction')) {
                  handleIntroduction(speech)
                } else {
                  handleCommand(speech)
                }
              }
            }
          },
          onUserSpeak: () => {}
        })
      }
    }

    const finishListening = () => {
      speechRecognizer.value.stop()
      flowFinished = true
      toggleHelpStage(false)
      toggleListening(false)
      togglePopupState(true)
      isRecognizing.value = false
    }

    const finishHandsFreeFlow = () => {
      updateCurrentStage('finish')
      finishListening()
      tracking.handsfreeEndFeature('stop cooking button click')
    }

    const openHelp = () => {
      togglePopupState(true)
      toggleHelpStage(true)
      updateCurrentStage('listening help')
    }

    const runCommand = () => {
      if (lastCommand.value && commands.value[lastCommand.value]) {
        commands.value[lastCommand.value]()
        lastCommand.value = null
      }
    }

    const beginListening = () => {
      if (!isError.value) {
        if (!isRecognizing.value) {
          try {
            speechRecognizer.value.start()
          } catch (e) {
            console.warn('[HandsFree] Cannot start recognizer:', e)
          }
        }
        updateCurrentStage('listening introduction')
      } else {
        updateCurrentStage('not-allowed')
      }
    }

    function requestMicrophoneAccess() {
      /* eslint-disable-next-line no-alert */
      alert('Microphone access is blocked. Please enable it in browser settings.')
      tracking.handsfreeOnboardingErrorMessage('microphone access is blocked')
    }

    const togglePopup = () => {
      if (state.isPopupOpened) {
        togglePopupState(false)
        tracking.handsfreeCollapseWidget()
      } else {
        togglePopupState(true)
        tracking.handsfreeExpandWidget()
      }
    }

    const continueListening = () => {
      togglePopupState(false)
      toggleHelpStage(false)
      updateCurrentStage('listening')
    }

    const closeHandsFreeFlow = () => {
      if (!state.isListening && speechRecognizer.value) {
        speechRecognizer.value.stop()
        updateCurrentStage(null)
      } else {
        togglePopup()
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)
      if (window.location.hash === '#getStatus') {
        getStatus.value = true
      }
    })

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside)
      try {
        if (speechRecognizer.value) {
          speechRecognizer.value.stop()
        }
      } catch (e) {
        console.warn('[HandsFree] Failed to stop on unmount:', e)
      }
      isRecognizing.value = false
    })

    const isRecognizing = ref(false)

    return {
      isPopupOpened,
      commands,
      Icon,
      micIcon,
      iconHelp,
      iconClose,
      currentStage,
      isListeningStage,
      isHelpStage,
      isNotRecognizedStage,
      isNotRecognizedStageError,
      isAlmostDone,
      isTooltipVisible,
      translations,
      tooltip,
      isLoading,
      openHelp,
      startHandsFreeFlow,
      finishListening,
      finishHandsFreeFlow,
      togglePopup,
      requestMicrophoneAccess,
      runCommand,
      beginListening,
      continueListening,
      closeHandsFreeFlow,

      selectedLanguage,
      printRecognizedSpeech,
      printWebSpeechStatus,
      printWebSpeechError,
      getStatus
    }
  }
}
/* eslint-enable */
</script>
